import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Header from '../../components/common/Header';
import Avatar from '../../components/common/Avatar';
import { InlineLoader } from '../../components/common/Loader';
import { updateStep1, updateStep2, updateStep3, updateStep4, getMyDoctorProfile, uploadClinicImages, deleteClinicImage, getDoctorQR } from '../../services/doctorService';
import { useAuth } from '../../context/AuthContext';

function StepTab({ step, current, label }) {
  const done = current > step; const active = current === step;
  return (
    <div className="flex flex-col items-center gap-0.5 flex-1">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${done ? 'bg-success text-white' : active ? 'bg-primary text-white' : 'bg-gray-100 text-text-muted'}`}>
        {done ? <i className="fa-solid fa-check text-[10px]" /> : step}
      </div>
      <span className={`text-[9px] font-medium hidden sm:block ${active ? 'text-primary' : 'text-text-muted'}`}>{label}</span>
    </div>
  );
}

export default function DoctorProfileSetup() {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [step, setStep]       = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const [s1, setS1] = useState({
    name: '', gender: '', phone: '', email: '', clinicName: '',
    medicineStaffName: '', medicineStaffNumber: '',
    address: '', locationCity: '', locationLat: null, locationLng: null, locationDetected: false,
    profileImg: null, previewImg: null,
  });
  const [clinicImages, setClinicImages] = useState([]);
  const [uploadingClinic, setUploadingClinic] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [quals, setQuals]       = useState([{ degree:'', specialization:'', college:'', hospital:'', experience:'' }]);
  const [bio, setBio]           = useState('');
  const [speciality, setSpeciality] = useState('');
  const [videoEnabled, setVideoEnabled] = useState(false);
  const [s4, setS4] = useState({
    consultationFee:'', cancellationCharge:'',
    discountEnabled:false, discountType:'flat', discountValue:'', discountValidityMonths:'0',
    accountHolder:'', accountNumber:'', ifsc:'', upiId:'',
    dailyEarningsTarget:'',
  });

  useEffect(() => {
    getMyDoctorProfile()
      .then(res => {
        const d = res.doctor; if (!d) return;
        setS1({
          name: d.name||'', gender: d.gender||'', phone: d.phone||'',
          email: d.email||'', clinicName: d.clinicName||'',
          medicineStaffName: d.medicineStaffName||'',
          medicineStaffNumber: d.medicineStaffNumber||'',
          address: d.location?.address || '',
          locationCity: d.location?.city || '',
          locationLat: d.location?.coordinates?.lat || null,
          locationLng: d.location?.coordinates?.lng || null,
          locationDetected: !!(d.location?.coordinates?.lat),
          profileImg: null, previewImg: d.profileImg||null,
        });
        if (d.qualifications?.length) setQuals(d.qualifications.map(q => ({ degree:q.degree||'', specialization:q.specialization||'', college:q.college||'', hospital:q.hospital||'', experience:q.experience||'' })));
        setBio(d.bio||''); setSpeciality(d.speciality||'');
        setVideoEnabled(!!d.videoConsultEnabled);
        setS4({ consultationFee:d.consultationFee||'', cancellationCharge:d.cancellationCharge||'', discountEnabled:d.oldPatientDiscount?.enabled||false, discountType:d.oldPatientDiscount?.type||'flat', discountValue:d.oldPatientDiscount?.value||'', discountValidityMonths: String(d.oldPatientDiscount?.validityMonths ?? 0), accountHolder:d.bankDetails?.accountHolder||'', accountNumber:d.bankDetails?.accountNumber||'', ifsc:d.bankDetails?.ifsc||'', upiId:d.bankDetails?.upiId||'', dailyEarningsTarget:d.dailyEarningsTarget||'' });
        setStep(d.setupStep || 1);
        if (d.clinicImages?.length) setClinicImages(d.clinicImages);
      })
      .catch(() => {})
      .finally(() => setFetching(false));
  }, []);

  useEffect(() => {
    setQrLoading(true);
    getDoctorQR()
      .then(res => setQrData(res))
      .catch(() => {})
      .finally(() => setQrLoading(false));
  }, []);

  const handleLogoChange = (file) => {
    if (!file) return;
    if (s1.previewImg?.startsWith('blob:')) URL.revokeObjectURL(s1.previewImg);
    setS1(p => ({ ...p, profileImg: file, previewImg: URL.createObjectURL(file) }));
  };

  const handleClinicImageUpload = async (files) => {
    if (!files.length) return;
    if (clinicImages.length + files.length > 5) { toast.error('Max 5 clinic images allowed'); return; }
    setUploadingClinic(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach(f => fd.append('images', f));
      const res = await uploadClinicImages(fd);
      setClinicImages(res.clinicImages || []);
      toast.success('Clinic images uploaded!');
    } catch (err) { toast.error(err.message); }
    finally { setUploadingClinic(false); }
  };

  const handleDeleteClinicImage = async (url) => {
    try {
      const res = await deleteClinicImage(url);
      setClinicImages(res.clinicImages || []);
      toast.success('Image removed');
    } catch (err) { toast.error(err.message); }
  };

  const saveStep1 = async () => {
    if (!s1.name || !s1.clinicName) { toast.error('Name and clinic name required'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      ['name','gender','clinicName','email','medicineStaffName','medicineStaffNumber'].forEach(k => { if (s1[k]) fd.append(k, s1[k]); });
      if (s1.profileImg) fd.append('image', s1.profileImg);
      // Build location object — merge text address + saved coordinates
      const locationObj = {
        address: s1.address || '',
        city: s1.locationCity || '',
        coordinates: s1.locationLat ? { lat: s1.locationLat, lng: s1.locationLng } : undefined,
      };
      fd.append('location', JSON.stringify(locationObj));
      const res = await updateStep1(fd);
      updateUser(res.doctor);
      toast.success('Personal details saved!');
      setStep(2);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const saveStep2 = async () => {
    if (!speciality) { toast.error('Speciality is required'); return; }
    setLoading(true);
    try {
      await updateStep2({ qualifications: quals.filter(q => q.degree || q.hospital), bio, speciality });
      toast.success('Qualifications saved!');
      setStep(3);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const saveStep3 = async () => {
    setLoading(true);
    try {
      await updateStep3({ videoConsultEnabled: videoEnabled });
      toast.success('Video settings saved!');
      setStep(4);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const saveStep4 = async () => {
    if (!s4.consultationFee) { toast.error('Consultation fee required'); return; }
    setLoading(true);
    try {
      await updateStep4({
        consultationFee: Number(s4.consultationFee),
        cancellationCharge: Number(s4.cancellationCharge)||0,
        oldPatientDiscount: { enabled: s4.discountEnabled, type: s4.discountType, value: Number(s4.discountValue)||0, validityMonths: Number(s4.discountValidityMonths)||0 },
        bankDetails: { accountHolder:s4.accountHolder, accountNumber:s4.accountNumber, ifsc:s4.ifsc, upiId:s4.upiId },
        dailyEarningsTarget: Number(s4.dailyEarningsTarget)||0,
      });
      toast.success('Profile complete!');
      navigate('/doctor/dashboard');
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const addQual  = () => setQuals(p => [...p, { degree:'', specialization:'', college:'', hospital:'', experience:'' }]);
  const removeQual = i => setQuals(p => p.filter((_,idx) => idx !== i));
  const updateQual = (i, k, v) => setQuals(p => p.map((q,idx) => idx===i ? { ...q, [k]:v } : q));

  if (fetching) return <div className="page-container-no-bottom"><Header title="Profile Setup" showBack /><InlineLoader /></div>;

  return (
    <div className="min-h-screen bg-surface pb-6">
      <Header title="Profile Setup" showBack onMenuClick={() => navigate('/doctor/dashboard')} />
      <div className="pt-14">
        {/* Step tabs */}
        <div className="bg-white border-b border-border px-4 py-3">
          <div className="flex items-center gap-2">
            {[['1','Personal'],['2','Education'],['3','Video'],['4','Payment']].map(([s,label],i) => (
              <React.Fragment key={s}>
                <StepTab step={Number(s)} current={step} label={label} />
                {i < 3 && <div className={`flex-1 h-0.5 ${step > Number(s) ? 'bg-success' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="px-4 py-5 space-y-4 max-w-lg mx-auto">

          {/* ── STEP 1 ── */}
          {step === 1 && (
            <>
              {/* Profile image */}
              <div className="flex flex-col items-center gap-2">
                <div className="relative">
                  <Avatar src={s1.previewImg} name={s1.name} size="xl" />
                  <label className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center cursor-pointer border-2 border-white">
                    <i className="fa-solid fa-camera text-white text-xs" />
                    <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) handleLogoChange(e.target.files[0]); }} />
                  </label>
                </div>
                <p className="text-xs text-text-muted">Profile Photo</p>
              </div>

              {/* ── Doctor QR Code Card ─────────────────────── */}
              {(qrLoading || qrData?.qrBase64) && (
                <div className="bg-gradient-to-br from-primary/5 to-teal-50 rounded-2xl border border-primary/20 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sm font-bold text-text-primary">Your Profile QR</p>
                      <p className="text-xs text-text-muted">Patients scan to book appointment</p>
                    </div>
                    {qrData?.qrBase64 && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            const a = document.createElement('a');
                            a.href = qrData.qrBase64;
                            a.download = 'faristaa-qr.png';
                            a.click();
                          }}
                          className="w-8 h-8 bg-primary/10 hover:bg-primary/20 rounded-xl flex items-center justify-center transition-colors"
                          title="Download QR">
                          <i className="fa-solid fa-download text-primary text-xs" />
                        </button>
                        <button
                          onClick={async () => {
                            if (navigator.share) {
                              try { await navigator.share({ title: 'Book my appointment', url: qrData.profileUrl }); } catch {}
                            } else {
                              navigator.clipboard.writeText(qrData.profileUrl);
                              import('react-hot-toast').then(m => m.default.success('Profile link copied!'));
                            }
                          }}
                          className="w-8 h-8 bg-primary/10 hover:bg-primary/20 rounded-xl flex items-center justify-center transition-colors"
                          title="Share">
                          <i className="fa-solid fa-share-nodes text-primary text-xs" />
                        </button>
                      </div>
                    )}
                  </div>
                  {qrLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="w-7 h-7 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-white rounded-xl border border-primary/20 shadow-sm flex-shrink-0">
                        <img src={qrData.qrBase64} alt="QR Code" className="w-20 h-20" />
                      </div>
                      <div className="space-y-1.5">
                        <p className="text-xs text-text-muted leading-relaxed">Print & display at clinic, or share on WhatsApp for patients to scan</p>
                        <p className="text-[10px] text-text-muted break-all opacity-70">{qrData.profileUrl}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {[
                { label:'Full Name *', key:'name', placeholder:'Dr. Full Name' },
                { label:'Clinic Name *', key:'clinicName', placeholder:'Your clinic name' },
                { label:'Phone', key:'phone', placeholder:'10-digit mobile' },
                { label:'Email', key:'email', placeholder:'email@example.com', type:'email' },
              ].map(f => (
                <div key={f.key}>
                  <label className="input-label">{f.label}</label>
                  <input type={f.type||'text'} value={s1[f.key]} onChange={e => setS1(p => ({...p,[f.key]:e.target.value}))} placeholder={f.placeholder} className="input" />
                </div>
              ))}

              <div>
                <label className="input-label">Gender</label>
                <div className="flex gap-2">
                  {['Male','Female','Other'].map(g => (
                    <button key={g} onClick={() => setS1(p => ({...p,gender:g}))}
                      className={`flex-1 py-2.5 rounded-btn border text-sm font-medium transition-all ${s1.gender===g ? 'border-primary bg-primary-light text-primary' : 'border-border text-text-secondary'}`}>{g}</button>
                  ))}
                </div>
              </div>

              {/* Clinic Images */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="input-label mb-0">Clinic Photos (max 5)</label>
                  <label className={`flex items-center gap-1.5 text-xs font-semibold text-primary cursor-pointer ${clinicImages.length >= 5 ? 'opacity-40 pointer-events-none' : ''}`}>
                    {uploadingClinic ? <i className="fa-solid fa-spinner animate-spin" /> : <i className="fa-solid fa-plus" />}
                    Add Photo
                    <input type="file" accept="image/*" multiple className="hidden"
                      onChange={e => handleClinicImageUpload(e.target.files)} />
                  </label>
                </div>
                {clinicImages.length > 0 ? (
                  <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
                    {clinicImages.map((img, i) => (
                      <div key={i} className="relative flex-shrink-0 w-20 h-20">
                        <img src={img} alt={'clinic '+i} className="w-full h-full object-cover rounded-xl border border-border" />
                        <button onClick={() => handleDeleteClinicImage(img)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-danger text-white rounded-full flex items-center justify-center text-[9px]">
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                    ))}
                    {clinicImages.length < 5 && (
                      <label className="flex-shrink-0 w-20 h-20 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-primary">
                        <i className="fa-solid fa-plus text-text-muted text-lg" />
                        <span className="text-[9px] text-text-muted mt-1">Add</span>
                        <input type="file" accept="image/*" multiple className="hidden"
                          onChange={e => handleClinicImageUpload(e.target.files)} />
                      </label>
                    )}
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary hover:bg-primary-light/30 transition-all">
                    <i className="fa-solid fa-camera text-text-muted text-xl mb-1" />
                    <span className="text-xs text-text-muted">Add clinic photos</span>
                    <input type="file" accept="image/*" multiple className="hidden"
                      onChange={e => handleClinicImageUpload(e.target.files)} />
                  </label>
                )}
                <p className="text-[10px] text-text-muted">These photos will be shown to patients on your profile</p>
              </div>

            {/* ── Location ─────────────────────────────────── */}
              <div>
                <label className="input-label">Clinic Address / Location</label>
                <div className="flex items-center gap-2 border border-border rounded-xl px-3 py-2.5 bg-white mb-2">
                  <i className="fa-solid fa-map-marker-alt text-primary text-sm flex-shrink-0" />
                  <input
                    value={s1.address}
                    onChange={e => setS1(p => ({...p, address: e.target.value}))}
                    placeholder="e.g. 123 MG Road, Bhopal, MP"
                    className="flex-1 bg-transparent outline-none text-sm text-text-primary"
                  />
                </div>
                <button type="button"
                  onClick={async () => {
                    if (!navigator.geolocation) { toast.error('Geolocation not supported'); return; }
                    const toastId = toast.loading('Detecting location...');
                    navigator.geolocation.getCurrentPosition(async pos => {
                      toast.dismiss(toastId);
                      const { latitude: lat, longitude: lng } = pos.coords;
                      try {
                        const r    = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`);
                        const data = await r.json();
                        const city = data.address?.city || data.address?.town || data.address?.village || data.address?.county || '';
                        const fullAddr = [data.address?.road, data.address?.suburb, city, data.address?.state].filter(Boolean).join(', ');
                        setS1(p => ({ ...p, locationCity: city, locationLat: lat, locationLng: lng, locationDetected: true,
                          address: p.address || fullAddr }));
                        toast.success(`Location: ${city}`);
                      } catch { toast.error('Could not reverse geocode location'); }
                    }, () => { toast.dismiss(toastId); toast.error('Location access denied'); }, { timeout: 8000 });
                  }}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                    s1.locationDetected
                      ? 'border-green-400 bg-green-50 text-green-700'
                      : 'border-dashed border-primary bg-primary-light text-primary'
                  }`}>
                  <i className={`fa-solid ${s1.locationDetected ? 'fa-circle-check' : 'fa-crosshairs'} text-sm`} />
                  {s1.locationDetected
                    ? `Live location saved${s1.locationCity ? ` — ${s1.locationCity}` : ''}`
                    : 'Use Live Location (Recommended)'}
                </button>
                {s1.locationDetected && (
                  <button type="button"
                    onClick={() => setS1(p => ({...p, locationDetected:false, locationLat:null, locationLng:null, locationCity:''}))}
                    className="mt-1.5 text-xs text-text-muted underline w-full text-center">
                    Clear live location
                  </button>
                )}
              </div>

            {/* Medicine Store Staff */}
              <div className="card-p space-y-3 bg-blue-50 border-blue-200">
                <p className="text-xs font-semibold text-info uppercase tracking-wide">
                  <i className="fa-solid fa-pills mr-1.5" />Medicine Store Staff (Optional)
                </p>
                <div>
                  <label className="input-label">Medicine Store Staff Name</label>
                  <input value={s1.medicineStaffName} onChange={e => setS1(p => ({...p,medicineStaffName:e.target.value}))}
                    placeholder="Staff name for medicine orders" className="input" />
                </div>
                <div>
                  <label className="input-label">Medicine Store Staff Number (WhatsApp/Phone)</label>
                  <input type="tel" value={s1.medicineStaffNumber} onChange={e => setS1(p => ({...p,medicineStaffNumber:e.target.value}))}
                    placeholder="10-digit mobile number" className="input" />
                  <p className="text-[10px] text-text-muted mt-1">Patients will be able to chat with this number on your profile</p>
                </div>
              </div>
            </>
          )}

          {/* ── STEP 2 ── */}
          {step === 2 && (
            <>
              <div>
                <label className="input-label">Speciality *</label>
                <input value={speciality} onChange={e => setSpeciality(e.target.value)} placeholder="e.g. General Physician" className="input" />
              </div>
              <div>
                <label className="input-label">Bio / About</label>
                <textarea value={bio} onChange={e => setBio(e.target.value)} rows={3} placeholder="Brief about yourself..." className="input resize-none" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="input-label mb-0">Qualifications</label>
                  <button onClick={addQual} className="text-xs text-primary font-semibold"><i className="fa-solid fa-plus mr-1" />Add</button>
                </div>
                {quals.map((q,i) => (
                  <div key={i} className="card-p space-y-2 mb-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-text-secondary">Qualification {i+1}</span>
                      {quals.length > 1 && <button onClick={() => removeQual(i)} className="text-danger text-xs"><i className="fa-solid fa-trash-can" /></button>}
                    </div>
                    {[['degree','Degree','MBBS, MD...'],['specialization','Specialization',''],['college','College',''],['hospital','Hospital','']].map(([k,l,ph]) => (
                      <input key={k} value={q[k]} onChange={e => updateQual(i,k,e.target.value)} placeholder={l+(ph?` (${ph})`:'')} className="input text-sm" />
                    ))}
                    <input type="number" value={q.experience} onChange={e => updateQual(i,'experience',e.target.value)} placeholder="Years of experience" className="input text-sm" />
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── STEP 3 ── */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="card-p">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-text-primary">Video Consultation</p>
                    <p className="text-xs text-text-muted mt-0.5">Allow patients to book video appointments</p>
                  </div>
                  <button onClick={() => setVideoEnabled(!videoEnabled)}
                    className={`w-12 h-6 rounded-full transition-all relative ${videoEnabled ? 'bg-primary' : 'bg-gray-200'}`}>
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${videoEnabled ? 'left-6' : 'left-0.5'}`} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 4 ── */}
          {step === 4 && (
            <>
              <div>
                <label className="input-label">Consultation Fee (₹) *</label>
                <input type="number" min={0} value={s4.consultationFee} onChange={e => setS4(p => ({...p,consultationFee:e.target.value}))} placeholder="e.g. 500" className="input" />
              </div>
              <div>
                <label className="input-label">Cancellation Charge (₹)</label>
                <input type="number" min={0} value={s4.cancellationCharge} onChange={e => setS4(p => ({...p,cancellationCharge:e.target.value}))} placeholder="0 = full refund" className="input" />
              </div>
              <div className="card-p space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-text-primary">Old Patient Discount</p>
                    <p className="text-xs text-text-muted">Auto-applied for returning patients</p>
                  </div>
                  <button onClick={() => setS4(p => ({...p,discountEnabled:!p.discountEnabled}))}
                    className={`w-12 h-6 rounded-full transition-all relative ${s4.discountEnabled ? 'bg-primary' : 'bg-gray-200'}`}>
                    <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${s4.discountEnabled ? 'left-6' : 'left-0.5'}`} />
                  </button>
                </div>
                {s4.discountEnabled && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      {['flat','percent'].map(t => (
                        <button key={t} onClick={() => setS4(p => ({...p,discountType:t}))}
                          className={`flex-1 py-2 rounded-btn border text-sm font-medium transition-all ${s4.discountType===t ? 'border-primary bg-primary-light text-primary' : 'border-border text-text-secondary'}`}>
                          {t==='flat' ? '₹ Flat' : '% Percent'}
                        </button>
                      ))}
                    </div>
                    <input type="number" min={0} value={s4.discountValue} onChange={e => setS4(p => ({...p,discountValue:e.target.value}))}
                      placeholder={s4.discountType==='flat' ? 'Discount in ₹' : 'Discount %'} className="input" />
                    {/* Validity window */}
                    <div className="mt-3">
                      <label className="input-label flex items-center gap-1.5">
                        <i className="fa-solid fa-clock text-primary text-xs" />
                        Discount Valid For
                      </label>
                      <select value={s4.discountValidityMonths}
                        onChange={e => setS4(p => ({...p, discountValidityMonths: e.target.value}))}
                        className="input mt-1">
                        <option value="0">Lifetime (no time limit)</option>
                        <option value="1">1 Month</option>
                        <option value="2">2 Months</option>
                        <option value="3">3 Months</option>
                        <option value="6">6 Months</option>
                        <option value="12">12 Months (1 Year)</option>
                        <option value="24">24 Months (2 Years)</option>
                      </select>
                      <p className="text-[10px] text-text-muted mt-1">
                        {s4.discountValidityMonths === '0' || !s4.discountValidityMonths
                          ? 'Discount applies on every revisit, no matter how long ago their last visit was.'
                          : `Discount only applies if patient returns within ${s4.discountValidityMonths} month${s4.discountValidityMonths === '1' ? '' : 's'} of their last visit.`
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <p className="input-label mb-2">Bank Details</p>
                <div className="space-y-2">
                  {[['upiId','UPI ID','yourname@upi'],['accountHolder','Account Holder',''],['accountNumber','Account Number',''],['ifsc','IFSC Code','']].map(([k,l,ph]) => (
                    <input key={k} value={s4[k]} onChange={e => setS4(p => ({...p,[k]:e.target.value}))} placeholder={l+(ph?` (${ph})`:'')} className="input text-sm" />
                  ))}
                </div>
              </div>
              <div>
                <label className="input-label">Daily Earnings Target (₹)</label>
                <input type="number" min={0} value={s4.dailyEarningsTarget} onChange={e => setS4(p => ({...p,dailyEarningsTarget:e.target.value}))} placeholder="Optional" className="input" />
              </div>
            </>
          )}

          {/* Navigation buttons */}
          <div className="flex gap-3 pt-2">
            {step > 1 && (
              <button onClick={() => setStep(step-1)} className="flex-1 py-3 border border-border rounded-btn text-sm font-semibold text-text-secondary">← Back</button>
            )}
            <button
              onClick={step===1 ? saveStep1 : step===2 ? saveStep2 : step===3 ? saveStep3 : saveStep4}
              disabled={loading}
              className="flex-1 py-3 bg-primary text-white rounded-btn text-sm font-semibold"
            >
              {loading
                ? <span className="flex items-center justify-center gap-2"><i className="fa-solid fa-spinner animate-spin" />Saving...</span>
                : step===4 ? 'Save & Finish ✓' : 'Save & Next →'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
